"use client";

import { useState } from "react";
import { useAction, useQuery } from "convex/react";
import { api } from "@convex/_generated/api";
import { CheckCircle2, Send } from "@/components/icons";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { submitFeedback } from "@/lib/feedback/submit-feedback";
import {
  gatherClientDiagnostics,
  formatDiagnosticsMarkdown,
} from "@/lib/feedback/client-diagnostics";
import type {
  ReportIssueContext,
  ReportIssueStatus,
} from "@/lib/feedback/types";
import { useSubmissionKey } from "@/lib/support/use-submission-key";

export type { ReportIssueContext, ReportIssueStatus };

interface ReportIssueFormProps {
  context?: ReportIssueContext;
  onSuccess?: () => void;
  variant?: "compact" | "inline";
  heading?: string;
}

function buildIssueTitle(context: ReportIssueContext | undefined): string {
  const titlePrefix = context?.titlePrefix ?? "Error report";
  return context?.issueRef
    ? `${titlePrefix} (${context.issueRef})`
    : context?.errorDigest
      ? `${titlePrefix} (digest: ${context.errorDigest})`
      : titlePrefix;
}

function buildErrorPrefix(context: ReportIssueContext | undefined): string {
  const errorPrefixParts: string[] = [];
  if (context?.errorMessage && context.errorMessage.trim().length > 0) {
    errorPrefixParts.push(`Error: ${context.errorMessage.trim()}`);
  }
  if (context?.errorDigest) {
    errorPrefixParts.push(`Digest: ${context.errorDigest}`);
  }
  return errorPrefixParts.length > 0 ? `${errorPrefixParts.join("\n")}\n\n` : "";
}

function resolveTrackingIds(
  context: ReportIssueContext | undefined,
  diagnostics: ReturnType<typeof gatherClientDiagnostics>,
) {
  const errorEventId = context?.errorEventId ?? diagnostics.errorEventId;
  const posthogReplayUrl =
    context?.posthogReplayUrl ?? diagnostics.posthogReplayUrl;
  const posthogSessionId =
    context?.posthogSessionId ?? diagnostics.posthogSessionId;
  return {
    errorEventId: errorEventId ?? undefined,
    posthogReplayUrl: posthogReplayUrl ?? undefined,
    posthogSessionId: posthogSessionId ?? undefined,
  };
}

export function ReportIssueForm({
  context,
  onSuccess,
  variant = "inline",
  heading = "Help us fix this issue",
}: ReportIssueFormProps) {
  const [description, setDescription] = useState("");
  const [email, setEmail] = useState("");
  const [status, setStatus] = useState<ReportIssueStatus>("idle");
  const createGitHubIssue = useAction(
    api.issueReports.actions.createGitHubIssue,
  );
  const submissionKey = useSubmissionKey();
  const currentUser = useQuery(api.users.queries.current);
  const signedInEmail = currentUser?.user?.email;

  async function handleSubmit(event: React.FormEvent) {
    event.preventDefault();
    if (!description.trim()) return;
    setStatus("submitting");

    try {
      const diagnostics = gatherClientDiagnostics();

      const pageUrl = context?.pageUrl ?? diagnostics.url;

      const title = buildIssueTitle(context);

      const userAgent = diagnostics.userAgent;

      const errorPrefix = buildErrorPrefix(context);

      const result = await submitFeedback({
        title,
        type: "bug",
        description: errorPrefix + description.trim(),
        email: signedInEmail ?? (email.trim() || undefined),
        pageUrl,
        errorDigest: context?.errorDigest,
        diagnostics: formatDiagnosticsMarkdown(diagnostics, {
          message: context?.errorMessage,
          digest: context?.errorDigest,
          stack: context?.errorStack,
        }),
        ...resolveTrackingIds(context, diagnostics),
        userAgent,
        submissionKey: submissionKey.current(),
        createGitHubIssue,
      });

      if (!result.convexOk) {
        setStatus("error");
        return;
      }

      setStatus("success");
      submissionKey.reset();
      onSuccess?.();
    } catch {
      setStatus("error");
    }
  }

  if (status === "success") {
    return (
      <div className="flex items-center gap-2 rounded-md border bg-muted/50 px-4 py-3 text-sm text-muted-foreground">
        <CheckCircle2 className="h-4 w-4 shrink-0 text-success-strong" />
        <span>Report sent.</span>
      </div>
    );
  }

  const textareaRows = variant === "compact" ? 4 : 3;

  return (
    <form onSubmit={handleSubmit} className="space-y-3 text-left">
      {heading && (
        <p className="text-sm font-medium text-foreground">{heading}</p>
      )}
      <Textarea
        value={description}
        onChange={(event) => setDescription(event.target.value)}
        placeholder="Describe what you were doing when this happened…"
        rows={textareaRows}
        required
        disabled={status === "submitting"}
      />
      {signedInEmail ? (
        <p className="text-xs text-muted-foreground">
          {"Sending as "}<span className="font-medium">{signedInEmail}</span>
        </p>
      ) : currentUser === undefined ? (
        <p className="text-xs text-muted-foreground">Loading account…</p>
      ) : (
        <Input
          type="email"
          value={email}
          onChange={(event) => setEmail(event.target.value)}
          placeholder="Email (optional — so we can follow up)"
          disabled={status === "submitting"}
        />
      )}
      <div className="flex items-center justify-end gap-2">
        {status === "error" && (
          <span className="mr-auto text-xs text-destructive-strong">
            Failed to send — please try again.
          </span>
        )}
        <Button variant="primary"
          type="submit"
          size="sm"
          disabled={
            !description.trim() ||
            status === "submitting" ||
            currentUser === undefined
          }
        >
          <Send className="mr-1.5 h-3.5 w-3.5" />
          {status === "submitting" ? "Sending…" : "Send to team"}
        </Button>
      </div>
    </form>
  );
}
